/**
 * Member self sign-up ("the member door", TRUS-778).
 *
 * Someone who needs help — food, a place to sleep, a bill — can ask for it
 * without an invitation from a care team: they pick what they need, give a
 * ZIP code, a first name and a mobile number, and confirm a code we text them.
 * The account that results has no password; next time they sign in with a
 * text code too.
 *
 * API calls here are unauthenticated except `getMyRequests`. The verify calls
 * return the same JSON as a member login and are stored by `storeMemberSession`
 * in lib/memberAuth.ts, so a joined member is signed in exactly like one who
 * came through an invitation.
 */

import { getWeaveinApiUrl } from '@/src/config';
import { getMemberToken, storeMemberSession, type MemberLoginResponse } from '@/lib/memberAuth';

/** The one help line (see lib/memberHelpLine.ts). */
export { useMemberHelpLine } from '@/lib/memberHelpLine';

/**
 * "Leave this site": a quick exit for someone who may be watched. `replace`
 * rather than a link, so the Back button does not return to this page.
 */
export const LEAVE_SITE_URL = 'https://www.google.com';
export function leaveSite(): void {
  window.location.replace(LEAVE_SITE_URL);
}

export type NeedCode =
  | 'food'
  | 'housing'
  | 'bills'
  | 'transportation'
  | 'health'
  | 'work_school'
  | 'family'
  | 'safety'
  | 'not_sure';

/** Need tiles, in display order. `labelKey` / `label` feed t(). */
export const NEEDS: { code: NeedCode; labelKey: string; label: string }[] = [
  { code: 'food', labelKey: 'memberJoin.need_food', label: 'Food' },
  { code: 'housing', labelKey: 'memberJoin.need_housing', label: 'A place to live' },
  { code: 'bills', labelKey: 'memberJoin.need_bills', label: 'Bills and money' },
  { code: 'transportation', labelKey: 'memberJoin.need_transportation', label: 'Rides' },
  { code: 'health', labelKey: 'memberJoin.need_health', label: 'Health care' },
  { code: 'work_school', labelKey: 'memberJoin.need_work_school', label: 'Work and school' },
  { code: 'family', labelKey: 'memberJoin.need_family', label: 'Family and kids' },
  { code: 'safety', labelKey: 'memberJoin.need_safety', label: 'Feeling safe' },
  { code: 'not_sure', labelKey: 'memberJoin.need_not_sure', label: "I'm not sure" },
];

export const NEED_CODES: NeedCode[] = NEEDS.map((n) => n.code);

export function isNeedCode(value: string | null | undefined): value is NeedCode {
  return !!value && (NEED_CODES as string[]).includes(value);
}

export type ContactMethod = 'text' | 'call';
export type BestTime = 'any' | 'morning' | 'afternoon' | 'evening';

export interface JoinStartRequest {
  first_name: string;
  phone: string;
  zip: string;
  needs: NeedCode[];
  answers: Record<string, string>;
  language: string;
  contact_method: ContactMethod;
  best_time: BestTime;
  safe_to_text: boolean;
  is_adult: boolean;
  qr_token?: string;
}

export interface JoinStartResponse {
  join_id: string;
  phone_masked: string;
  expires_in?: number;
}

export interface JoinRequestSummary {
  id: number | string;
  need: string;
  status: string;
}

export type JoinVerifyResponse = MemberLoginResponse & { requests?: JoinRequestSummary[] };

export interface NearbyPlace {
  name: string;
  address?: string | null;
  phone?: string | null;
  distance_miles?: number | null;
  hours?: string | null;
  website?: string | null;
}

export type MemberRequestStatus = 'asked' | 'matched' | 'connected' | 'closed';

export interface MemberRequest {
  id: number | string;
  need: string;
  status: MemberRequestStatus | string;
  org_name?: string | null;
  updated_at?: string | null;
}

/**
 * An error the screens can show as-is. `status` is the HTTP status (0 for a
 * network failure); a 404/5xx means the service is not reachable yet, and the
 * screen offers the help line instead of a dead end.
 */
export class MemberJoinError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function postJson<T>(path: string, body: unknown, token?: string | null): Promise<T> {
  let res: Response;
  try {
    res = await fetch(getWeaveinApiUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new MemberJoinError('', 0);
  }
  return readJson<T>(res);
}

async function getJson<T>(path: string, token?: string | null): Promise<T> {
  let res: Response;
  try {
    res = await fetch(getWeaveinApiUrl(path), {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(token ? { 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '' } : {}),
      },
    });
  } catch {
    throw new MemberJoinError('', 0);
  }
  return readJson<T>(res);
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Only 4xx validation messages are meant for the person on the screen;
    // 404 and 5xx are "not available right now" and get the friendly copy.
    const detail =
      res.status >= 400 && res.status < 500 && res.status !== 404 && typeof body?.detail === 'string'
        ? body.detail
        : '';
    throw new MemberJoinError(detail, res.status);
  }
  return res.json() as Promise<T>;
}

/** Digits only, without a leading US country code. */
export function normalizeUsPhone(input: string): string {
  const digits = input.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

export function isValidUsPhone(input: string): boolean {
  return normalizeUsPhone(input).length === 10;
}

export function isValidZip(input: string): boolean {
  return /^\d{5}$/.test(input.trim());
}

export function joinStart(body: JoinStartRequest): Promise<JoinStartResponse> {
  return postJson<JoinStartResponse>('/api/member-portal/join/start', body);
}

export function joinResend(joinId: string): Promise<{ phone_masked: string }> {
  return postJson<{ phone_masked: string }>('/api/member-portal/join/resend', { join_id: joinId });
}

/** Confirms the code and signs the new member in (stored like a member login). */
export async function joinVerify(joinId: string, code: string): Promise<JoinVerifyResponse> {
  const result = await postJson<JoinVerifyResponse>('/api/member-portal/join/verify', {
    join_id: joinId,
    code,
    consent: true,
  });
  storeMemberSession(result);
  return result;
}

export async function getJoinPlaces(need: string, zip: string): Promise<NearbyPlace[]> {
  const qs = new URLSearchParams({ need, zip }).toString();
  const data = await getJson<NearbyPlace[] | { places?: NearbyPlace[] }>(
    `/api/member-portal/join/places?${qs}`,
  );
  return Array.isArray(data) ? data : data?.places ?? [];
}

/** The signed-in member's requests for help. Throws when not signed in. */
export async function getMyRequests(): Promise<MemberRequest[]> {
  const token = getMemberToken();
  if (!token) throw new MemberJoinError('', 401);
  const data = await getJson<MemberRequest[] | { requests?: MemberRequest[] }>(
    '/api/member-portal/my-requests',
    token,
  );
  return Array.isArray(data) ? data : data?.requests ?? [];
}

export function loginPhoneStart(phone: string): Promise<{ login_id: string; phone_masked: string }> {
  return postJson<{ login_id: string; phone_masked: string }>('/api/member-portal/login/phone/start', {
    phone,
  });
}

/** Confirms a sign-in code and stores the session like the email login does. */
export async function loginPhoneVerify(loginId: string, code: string): Promise<MemberLoginResponse> {
  const result = await postJson<MemberLoginResponse>('/api/member-portal/login/phone/verify', {
    login_id: loginId,
    code,
  });
  storeMemberSession(result);
  return result;
}

/**
 * An invite code as typed or pasted: either the bare token, or a link that
 * contains `/member/register/<token>` (or the legacy `?token=`). Returns the
 * token, or '' when nothing usable was entered.
 */
export function parseInviteCode(input: string): string {
  const value = input.trim();
  if (!value) return '';
  const path = value.match(/\/member\/register\/([^/?#\s]+)/);
  if (path) return decodeURIComponent(path[1]);
  const query = value.match(/[?&]token=([^&#\s]+)/);
  if (query) return decodeURIComponent(query[1]);
  if (/^https?:\/\//i.test(value) || value.includes('/')) return '';
  return value.replace(/\s+/g, '');
}
